// #83 StateBanner: the requisition's administrative state above its tabs. On Hold (a gold banner with the paused and
// continued activities and their dates, and Days on Hold) · Cancellation Pending / Reopen Pending (gold, waiting) ·
// Cancelled / Archived (a neutral banner with the closure date, the reporting category and *Request reopen*) ·
// Reopened (a blue banner naming the prior closure). The action follows the button rules: absent without the right,
// blocked with his reason (DC-24). The pending forms (B2-4) name who asked and when (`requested`, actor · a <time>) and
// take the approver's controls in `actions` (the HR Business Partner approves); `taManagerNotified` draws his *TA Manager
// notified* line (G §3: hold, cancel and reopen), with its date when given, on every state but Archived.

import type { ReactNode } from "react";
import { Banner, type BannerTone } from "../core/Banner";
import { Button, type Blocked } from "../core/Button";
import { KeyValue, type KeyValueItem } from "../core/KeyValue";
import { LABEL_TEMPLATES, REQUISITION_STATUS, SLA_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { DEFAULT_TIME_ZONE, formatDate, toEpoch, type Instant } from "../lib/time";
import { STATE_BANNER_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export type StateBannerKind = "onHold" | "cancellationPending" | "cancelled" | "archived" | "reopenPending" | "reopened";

export interface StateBannerProps {
  kind: StateBannerKind;
  /** When the state began (the hold, the cancellation, the archive, the reopen). */
  since?: Instant | undefined;
  timeZone?: string | undefined;
  /** On Hold: the activities the hold paused and the ones that continue (each a line, with its date). */
  paused?: readonly ReactNode[] | undefined;
  continued?: readonly ReactNode[] | undefined;
  daysOnHold?: number | undefined;
  /** Cancelled / Archived: the reporting category. */
  reportingCategory?: ReactNode | undefined;
  /** Reopened: the closure it reopened (his status word, Cancelled or Filled) and its date. */
  priorClosure?: { word: string; at?: Instant | undefined } | undefined;
  onRequestReopen?: (() => void) | undefined;
  /** The seat may request a reopen (default true when onRequestReopen is given). */
  canReopen?: boolean | undefined;
  reopenBlocked?: Blocked | undefined;
  /** Cancellation Pending / Reopen Pending: who asked and when. */
  requested?: { by: string; at?: Instant | undefined } | undefined;
  /** Cancellation Pending / Reopen Pending: the approver's controls (the seat's buttons), right of the banner. */
  actions?: ReactNode | undefined;
  /** *TA Manager notified*: true, or the time the notice went (drawn as its date). Not on Archived. */
  taManagerNotified?: boolean | { at: Instant } | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

const TONE: Readonly<Record<StateBannerKind, BannerTone>> = {
  onHold: "warn", cancellationPending: "warn", reopenPending: "warn", cancelled: "neutral", archived: "neutral", reopened: "info",
};

const WORD: Readonly<Record<StateBannerKind, string>> = {
  onHold: REQUISITION_STATUS.onHold, cancellationPending: REQUISITION_STATUS.cancellationPending, cancelled: REQUISITION_STATUS.cancelled,
  archived: REQUISITION_STATUS.archived, reopenPending: REQUISITION_STATUS.reopenPending, reopened: REQUISITION_STATUS.reopened,
};

const lines = (items: readonly ReactNode[]) => <span className="mc-stb-lines">{items.map((item, index) => <span key={index}>{item}</span>)}</span>;

export function StateBanner({
  kind, since, timeZone = DEFAULT_TIME_ZONE, paused, continued, daysOnHold, reportingCategory, priorClosure, onRequestReopen, canReopen,
  reopenBlocked, requested, actions, taManagerNotified, hs, className,
}: StateBannerProps) {
  const closed = kind === "cancelled" || kind === "archived";
  const pendingKind = kind === "cancellationPending" || kind === "reopenPending";
  const stamp = (at: Instant) => <time dateTime={new Date(toEpoch(at)).toISOString()}>{formatDate(at, timeZone)}</time>;
  const notified = kind !== "archived" && taManagerNotified ? (
    <span className="mc-stb-tn">
      {typeof taManagerNotified === "object" ? <>{`${STATE_BANNER_LABELS.taManagerNotified} · `}{stamp(taManagerNotified.at)}</> : STATE_BANNER_LABELS.taManagerNotified}
    </span>
  ) : null;
  const date = since !== undefined ? formatDate(since, timeZone) : undefined;
  const items: KeyValueItem[] = [];
  if (kind === "onHold") {
    if (paused?.length) items.push([STATE_BANNER_LABELS.paused, lines(paused)]);
    if (continued?.length) items.push([STATE_BANNER_LABELS.continued, lines(continued)]);
    if (daysOnHold !== undefined) items.push([SLA_LABELS.daysOnHold, <span className="mc-stb-n">{daysOnHold}</span>]);
  }
  if (closed && reportingCategory !== undefined && reportingCategory !== null) items.push([STATE_BANNER_LABELS.reportingCategory, reportingCategory]);
  const action = closed && onRequestReopen ? (
    <Button sm can={canReopen ?? true} blocked={reopenBlocked} onClick={onRequestReopen} hs={hs}>{STATE_BANNER_LABELS.requestReopen}</Button>
  ) : pendingKind && actions !== undefined && actions !== null && actions !== false ? <span className="mc-stb-a">{actions}</span> : undefined;
  return (
    <div className={cx("mc-stb", `mc-stb-${kind}`, className)}>
      <Banner tone={TONE[kind]} icon={kind === "onHold" ? "clock" : kind === "reopened" ? "refresh" : undefined} right={action}>
        <span className="mc-stb-h">
          {kind === "archived" && date !== undefined
            ? <b>{LABEL_TEMPLATES.archivedOn({ date })}</b>
            : <><b>{WORD[kind]}</b>{date !== undefined ? <span className="mc-stb-d">{date}</span> : null}</>}
          {kind === "reopened" && priorClosure ? (
            <span className="mc-stb-prior">
              <span>{priorClosure.word}</span>
              {priorClosure.at !== undefined ? <span>{formatDate(priorClosure.at, timeZone)}</span> : null}
            </span>
          ) : null}
          {pendingKind && requested ? (
            <span className="mc-stb-by">{requested.at !== undefined ? <>{`${requested.by} · `}{stamp(requested.at)}</> : requested.by}</span>
          ) : null}
        </span>
        {notified}
        {items.length ? <KeyValue rows items={items} /> : null}
      </Banner>
    </div>
  );
}
